import toast from "react-hot-toast";
import axiosInstance from "./Axios";
import { clientRoutes } from "../routes";

export class AgencyApi {
  static getAgencyList = async ({
    page,
    search,
    principal_agent_id,
    registration_status,
    status,
    start_date,
    end_date,
    city,
    country_code,
  }: {
    page: number;
    search: string;
    principal_agent_id: string;
    registration_status: string;
    status: string;
    start_date: string;
    end_date: string;
    city: string;
    country_code?: string;
  }) => {
    try {
      const res = await axiosInstance.get(`admin/agency`, {
        params: {
          page,
          search,
          limit: 30,
          principal_agent_id,
          registration_status,
          status,
          start_date,
          end_date,
          city,
          country_code,
        },
      });

      return {
        data: res.data,
        success: true,
      };
    } catch (err: any) {
      const status = err?.status;

      if (status === 401) {
        toast.error("Unauthorized, please login!");
        window.location.href = clientRoutes.login;
      }

      return {
        error: "Something went wrong !",
        data: err?.response?.data,
        success: false,
      };
    }
  };

   static validateTaxId = async (data: {
    agency_id: string;
    value: string;
    type?: string;
  }) => {
    try {
      const res = await axiosInstance.post(`agency/validate-tax-id`, data);

      return {
        data: res.data,
        success: true,
      };
    } catch (err: any) {
      return {
        error: err?.response?.data?.message || "Invalid Tax ID Number",
        data: err?.response?.data,
        success: false,
      };
    }
  };

  static verifyAgencyPhone = async (agency_id: string) => {
    try {
      const res = await axiosInstance.post(
        `admin/agency/verify-phone/${agency_id}`,
      );

      return {
        data: res.data,
        success: true,
      };
    } catch (err: any) {
      const status = err?.response?.status;

      if (status === 401) {
        toast.error("Unauthorized, please login!");
        window.location.href = clientRoutes.login;
      }

      return {
        error: "Something went wrong !",
        data: err?.response?.data,
        success: false,
      };
    }
  };

  static agencyById = async (agency_id: string) => {
    try {
      const res = await axiosInstance.get(`admin/agency/${agency_id}`);

      return {
        data: res.data,
        success: true,
      };
    } catch (err: any) {
      const status = err?.status;

      if (status === 401) {
        toast.error("Unauthorized, please login!");
        window.location.href = clientRoutes.login;
      }

      return {
        error: "Something went wrong !",
        data: err?.response?.data,
        success: false,
      };
    }
  };

  static async getAgentsByAgencyId({
    page,
    search,
    agency_ref,
    registration_status,
    status,
    start_date,
    end_date,
    city,
  }: {
    page: number;
    search: string;
    agency_ref: string;
    registration_status: string;
    status: string;
    start_date: string;
    end_date: string;
    city: string;
  }) {
    try {
      if (!agency_ref) {
        throw new Error("Agency reference is required");
      }

      const response = await axiosInstance.get(`agency/${agency_ref}/agents`, {
        params: {
          page,
          search,
          limit: 10,
          registration_status,
          status,
          start_date,
          end_date,
          city,
        },
      });

      const res = response?.data;

      if (res?.success) {
        return {
          success: true,
          data: res.data, // includes: docs[], total, totalPages, page, limit
        };
      }

      throw new Error(res?.message || "Failed to fetch agents");
    } catch (err: any) {
      console.error("API Error:", err);
      if (err?.response?.status === 401) {
        toast.error("Session expired, please login again");
        window.location.href = clientRoutes.login;
      }

      return {
        success: false,
        error:
          err?.response?.data?.message || err.message || "API request failed",
      };
    }
  }

  static SearchAgencyList = async (
    search: string,
    limit?: number,
    registration_status?: string,
  ) => {
    try {
      let url = `admin/agency?search=${search}`;

      if (limit) {
        url += `&limit=${limit}`;
      }

      if (registration_status) {
        url += `&registration_status=${registration_status}`;
      }

      const res = await axiosInstance.get(url);
      return {
        data: res.data,
      };
    } catch (err: any) {
      return {
        error: "Something went wrong !",
        data: err?.response?.data,
      };
    }
  };

  static AgencyLogo = async (data: any) => {
    try {
      const res = await axiosInstance.post(`agency/upload-agency-logo`, data);
      return {
        data: res.data,
      };
    } catch (err: any) {
      return {
        error: "Something went wrong !",
        data: err?.response?.data,
      };
    }
  };

  static AgencyLogoColor = async (data: any) => {
    try {
      const res = await axiosInstance.post(
        `agency/upload-agency-logo-color`,
        data,
      );
      return {
        data: res.data,
      };
    } catch (err: any) {
      return {
        error: "Something went wrong !",
        data: err?.response?.data,
      };
    }
  };

  static RegisterAgency = async (data: any) => {
    try {
      const res = await axiosInstance.post(`agency/register/profile`, data);
      return {
        data: res.data,
      };
    } catch (err: any) {
      return {
        error: "Something went wrong !",
        data: err?.response?.data,
      };
    }
  };

  static updateAgencyProfile = async (data: any, agency_id: string) => {
    try {
      const res = await axiosInstance.post(
        `admin/agency/update/${agency_id}`,
        data,
      );
      return {
        data: res.data,
      };
    } catch (err: any) {
      return {
        error: "Something went wrong !",
        data: err?.response?.data,
      };
    }
  };

  static updateAgencyImage = async (data: any, agency_id: string) => {
    try {
      const res = await axiosInstance.post(
        `admin//agency/update/${agency_id}`,
        data,
      );
      return {
        data: res.data,
      };
    } catch (err: any) {
      return {
        error: "Something went wrong !",
        data: err?.response?.data,
      };
    }
  };

  static updateAgencyLogo = async (file: File, agency_id: string) => {
    try {
      const formData = new FormData();
      formData.append("agency_logo", file);

      const res = await axiosInstance.post(
        `/agency/update-agency-logo/${agency_id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      return {
        data: res.data,
      };
    } catch (err: any) {
      return {
        error: "Something went wrong!",
        data: err?.response?.data,
      };
    }
  };

  static AgencyInvitationLink = async (id: string) => {
    try {
      const res = await axiosInstance.post(
        `agent/get-invitation-link?inviter_id=${id}`,
      );
      return {
        data: res.data,
      };
    } catch (err: any) {
      return {
        error: "Something went wrong !",
        data: err?.response?.data,
      };
    }
  };

  static AgencyInvite = async (data: {
    invitee_email: string;
    inviter_id: string;
  }) => {
    try {
      const res = await axiosInstance.post(`agent/invite-agency`, data);
      return {
        data: res.data,
      };
    } catch (err: any) {
      return {
        error: "Something went wrong !",
        data: err?.response?.data,
      };
    }
  };

  static getAgencyById = async (params: { agency_id: string }) => {
    try {
      const res = await axiosInstance.get(
        `agency/agency-details/${params.agency_id}`,
      );

      return {
        data: res.data,
        success: true,
      };
    } catch (err: any) {
      const status = err?.status;

      if (status === 401) {
        toast.error("Unauthorized, please login!");
        window.location.href = clientRoutes.login;
      }

      return {
        error: "Something went wrong !",
        data: err?.response?.data,
        success: false,
      };
    }
  };

  static getAgencyDetails = async () => {
    try {
      const res = await axiosInstance.get(`agency/agency-details`);

      return {
        data: res.data,
        success: true,
      };
    } catch (err: any) {
      const status = err?.status;

      if (status === 401) {
        toast.error("Unauthorized, please login!");
        window.location.href = clientRoutes.login;
      }

      return {
        error: "Something went wrong !",
        data: err?.response?.data,
        success: false,
      };
    }
  };

  static createAgentProfile = async (data: any) => {
    try {
      const res = await axiosInstance.post(
        `agency/register/create-agent-profile`,
        data,
      );

      return {
        data: res.data,
        success: true,
      };
    } catch (err: any) {
      return {
        error: "Something went wrong !",
        data: err?.response?.data,
      };
    }
  };

  static manageJoiningRequest = async (data: {
    agencyId: string;
    action: "approve" | "reject" | "block";
    notes: string;
  }) => {
    try {
      const res = await axiosInstance.post(`admin/agency/manage-request`, data);

      return {
        data: res.data,
        success: true,
      };
    } catch (err: any) {
      const status = err?.response?.status;

      if (status === 401) {
        toast.error("Unauthorized, please login!");
        window.location.href = clientRoutes.login;
      }

      return {
        error: "Something went wrong !",
        data: err?.response?.data,
        success: false,
      };
    }
  };
}
